import { faBars } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC,
  useCallback,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { HOME_ROUTE } from '~/_shared/constants/routes';
import { useTheme } from '~/_shared/themes/theme.hooks';
import {
  type MainMenuItem,
  MainMenuItems,
} from '~/mainMenu/mainMenuItem.enum';
import {
  containerStyle,
  headingSectionStyle,
  primarySectionStyle,
  rightIconStyle,
  rightSectionStyle,
  underbarStyle,
} from '~/mobileTopBar/mobileTopBar.styles';
import { useSlidingAnimationMsDuration } from '~/store/frontendState/graphicSettings/graphicSettings.selectors';
import { type MapSettingsPublicMapSettingsState } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.state';
import { HeadingComponent } from '~/topBar/heading/heading.component';
import { type NavigationItem } from '~/topBar/navigation/navigation.helpers';
import { ReadOnlyWarningComponent } from '~/topBar/readOnlyWarning.component';
import { MobileNavigationContainer } from './mobileNavigation/mobileNavigation.container';
import { MobileTopUnderbarContainer } from './mobileTopUnderbar/mobileTopUnderbar.container';

export const mobileMapToolsAsMenuItems = new Set<MainMenuItem>([
  MainMenuItems.Grouping,
  MainMenuItems.Filter,
  MainMenuItems.Routing,
  MainMenuItems.Proximity,
]);

export const mobileRightSideButtonsMenuItems = new Set<MainMenuItem>([
  MainMenuItems.MapKey,
  MainMenuItems.Search,
  MainMenuItems.NearMe,
]);

type AppBarProps = Readonly<{
  accountName?: string;
  activeNavigationItem: NavigationItem;
  hasAvailableTools: boolean;
  isMapPresentational: boolean;
  isReadOnlyModeWarningEnabled: boolean;
  mapDescription?: string;
  mapLink?: string;
  mapName?: string;
  mapViewName?: string;
  topUnderbarOpen: boolean;
  publicMapSettings?: MapSettingsPublicMapSettingsState;

  hideSearchBar: () => void;
  toggleTopBar: (isOpen: boolean) => void;
}>;

export const MobileTopBarComponent: FC<AppBarProps> = ({ toggleTopBar, ...props }) => {
  const slidingAnimationDurationMs = useSlidingAnimationMsDuration();
  const theme = useTheme();

  const toggleBottom = useCallback(() => {
    const hideSearchBar = props.hideSearchBar;
    if (props.topUnderbarOpen) {
      hideSearchBar();
    }
    toggleTopBar(!props.topUnderbarOpen);
  }, [props.hideSearchBar, props.topUnderbarOpen, toggleTopBar]);

  return (
    <div>
      <div css={containerStyle({ theme })}>
        <div css={[primarySectionStyle, { flexShrink: 0 }]} >
          {!props.isMapPresentational && (<MobileNavigationContainer />)}
        </div>

        {props.isReadOnlyModeWarningEnabled && (
          <div css={[primarySectionStyle, { flexGrow: 1 }]}>
            <ReadOnlyWarningComponent />
          </div>
        )}

        <div css={[headingSectionStyle, { flexShrink: 1 }]} >
          {(!props.publicMapSettings || props.publicMapSettings.displayTitleAndDescription) && (
            <HeadingComponent
              accountName={props.mapName ? '' : props.accountName}
              mapDescription={props.mapDescription}
              mapName={props.mapName}
              mapLink={props.mapLink}
              mapViewName={props.mapViewName}
            />
          )}
        </div>

        <div css={[rightSectionStyle, { flexShrink: 0 }]} >
          {(props.hasAvailableTools && props.isMapPresentational && !props.publicMapSettings?.mapToolsMenuOpen
              || props.activeNavigationItem.path !== HOME_ROUTE) &&
            (
              <div onClick={toggleBottom}>
                <FontAwesomeIcon
                  icon={faBars}
                  css={rightIconStyle}
                />
              </div>
            )}
        </div>
      </div>

      <div
        css={underbarStyle({ isVisible: props.topUnderbarOpen, durationMs: slidingAnimationDurationMs })}
      >
        <MobileTopUnderbarContainer />
      </div>
    </div>
  );
};
